
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

type Review = {
  id: string;
  name: string;
  position: string;
  company: string;
  rating: number;
  text: string;
  status: string;
  created_at: string;
}

type UseReviewsReturn = {
  approveReview: (id: string) => Promise<void>;
  archiveReview: (id: string) => Promise<void>;
  fetchReviewsForApproval: () => Promise<Review[]>;
  fetchApprovedReviews: () => Promise<Review[]>;
  fetchArchivedReviews: () => Promise<Review[]>;
  fetchPublicReviews: () => Promise<Review[]>;
  isLoading: boolean;
  error: string | null;
}

export const useReviews = (): UseReviewsReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approveReview = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ status: 'approved' })
        .eq('id', id);
        
      if (error) throw error;
    } catch (err) {
      console.error('Error approving review:', err);
      setError('Failed to approve review');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const archiveReview = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ status: 'archived' })
        .eq('id', id);
        
      if (error) throw error;
    } catch (err) {
      console.error('Error archiving review:', err);
      setError('Failed to archive review');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchReviewsForApproval = async (): Promise<Review[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('id, name, position, company, rating, text, status, created_at')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      return data || [];
    } catch (err) {
      console.error('Error fetching pending reviews:', err);
      setError('Failed to fetch pending reviews');
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchApprovedReviews = async (): Promise<Review[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('id, name, position, company, rating, text, status, created_at')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      return data || [];
    } catch (err) {
      console.error('Error fetching approved reviews:', err);
      setError('Failed to fetch approved reviews');
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchArchivedReviews = async (): Promise<Review[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('id, name, position, company, rating, text, status, created_at')
        .eq('status', 'archived')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      return data || [];
    } catch (err) {
      console.error('Error fetching archived reviews:', err);
      setError('Failed to fetch archived reviews');
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Fetches publicly approved reviews with high ratings (4+)
   * This function uses anon access to bypass RLS for public reviews
   */
  const fetchPublicReviews = async (): Promise<Review[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Fetching public reviews from Supabase...');
      
      // Add retry logic for network issues
      const maxRetries = 2;
      let retries = 0;
      let success = false;
      let data = null;
      let fetchError = null;
      
      while (!success && retries <= maxRetries) {
        try {
          const response = await supabase
            .from('reviews')
            .select('id, name, position, company, rating, text, status, created_at')
            .eq('status', 'approved')
            .gte('rating', 4)
            .order('created_at', { ascending: false });
            
          if (response.error) {
            fetchError = response.error;
            retries++;
            if (retries <= maxRetries) {
              // Wait a bit before retrying (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retries)));
              console.log(`Retry attempt ${retries}/${maxRetries} for public reviews`);
            }
          } else {
            data = response.data;
            success = true;
          }
        } catch (err) {
          fetchError = err;
          retries++;
          if (retries <= maxRetries) {
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retries)));
            console.log(`Retry attempt ${retries}/${maxRetries} for public reviews after error`, err);
          }
        }
      }
      
      if (!success) {
        console.error('Error fetching public reviews after retries:', fetchError);
        setError('Failed to fetch reviews. Please refresh the page or try again later.');
        // Still return empty array so UI can handle gracefully
        return [];
      }
      
      console.log('Public reviews fetched successfully:', data);
      return data || [];
    } catch (err) {
      console.error('Exception fetching public reviews:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    approveReview,
    archiveReview,
    fetchReviewsForApproval,
    fetchApprovedReviews,
    fetchArchivedReviews,
    fetchPublicReviews,
    isLoading,
    error
  };
};
